import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  useState,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
} from "react";
import Text from "@/components/Text";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { COLUMNS_GRID_BUILDER } from "@/lib/constants";
import { useTranslation } from "react-i18next";

type Props = {
  children: ReactNode;
  columns: string[];
  setColumns: Dispatch<SetStateAction<string[]>>;
  initColumns: string[];
  updateGridCallback: (cols: string[]) => void;
};

const DrawerColumns: FC<Props> = ({
  children,
  setColumns,
  columns,
  initColumns,
  updateGridCallback,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const onChangeColumn = (state: boolean, columnName: string) => {
    if (state) {
      setColumns([...columns, columnName]);
    } else {
      setColumns(columns.filter((column) => column !== columnName));
    }
  };

  const compareColumns = () => {
    let same = true;

    COLUMNS_GRID_BUILDER.forEach((column) => {
      if (initColumns.includes(column) !== columns.includes(column)) {
        same = false;
      }
    });

    return same;
  };

  return (
    <Drawer
      direction="right"
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          if (!compareColumns()) setColumns(initColumns);
        }
        setOpen(open);
      }}
    >
      <DrawerTrigger>{children}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader
          style={{
            borderBottom: "1px solid #F1F2F4",
          }}
        >
          <div className="flex justify-between items-center">
            <Text fontSize={24} fontWeight={600}>
              {t("columns")}
            </Text>
            <X
              height={20}
              width={20}
              className="cursor-pointer"
              onClick={() => {
                if (!compareColumns()) setColumns(initColumns);
                setOpen(false);
              }}
            />
          </div>
        </DrawerHeader>

        <div className="flex flex-col gap-[32px] p-[24px]">
          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_score_performance")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("krunsh_score")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "krunsh_score")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("krunsh_score")}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("rank")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "rank")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("rank")}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_reviews")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("rating")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "rating")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("average_rating")}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("reviews_count")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "reviews_count")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("rating_count")}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_delivery")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("delivery_time")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "delivery_time")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("average_time")}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_promotions")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("promotion_active")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "promotion_active")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("active_promotion")}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("promotion_type")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "promotion_type")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("promotion_type")}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_visuals")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("menu_items")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "menu_items")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("menu_items")}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("hero_image")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "hero_image")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("hero_image")}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text
              fontSize={14}
              fontWeight={400}
              color="#44546F"
              className="uppercase"
            >
              {t("general")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("shifts")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "shifts")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("opening_hours")}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={columns.includes("categories")}
                onCheckedChange={(state) =>
                  onChangeColumn(state as boolean, "categories")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("categories")}
              </Text>
            </div>
          </div>
        </div>

        <DrawerFooter
          style={{
            borderTop: "1px solid #F1F2F4",
          }}
        >
          <div className="flex justify-end gap-[8px]">
            <Button
              variant="secondaryFilled"
              onClick={() => {
                setColumns(initColumns);
                setOpen(false);
              }}
            >
              {t("reset")}
            </Button>
            <Button
              onClick={() => {
                updateGridCallback(columns);
                setOpen(false);
              }}
            >
              {t("apply")}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerColumns;
